import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Badge component following Figma design system specifications
 * Based on 2-Tokens.json and 3-Componentes.json design tokens
 * Uses lighter, more accessible colors from Figma palette
 * Supports default, compact, and dot variants
 */
const badgeVariants = cva(
  cn(
    'inline-flex items-center justify-center border font-geist font-medium transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-2'
  ),
  {
    variants: {
      variant: {
        // Primary: slate-300 bg + slate-800 text (from Figma badge-primary tokens)
        default:
          'border-transparent bg-slate-300 text-slate-800 shadow-sm focus:ring-slate-400',
        // Secondary: neutral-200 bg + neutral-800 text
        secondary:
          'border-transparent bg-neutral-200 text-neutral-800 focus:ring-neutral-300',
        // Danger/Destructive: red-200 bg + red-800 text (from Figma badge-danger tokens)
        destructive:
          'border-transparent bg-red-200 text-red-800 shadow-sm focus:ring-red-300',
        // Success: green-200 bg + green-800 text (from Figma badge-success tokens)
        success:
          'border-transparent bg-green-200 text-green-800 shadow-sm focus:ring-green-300',
        // Warning: yellow-100 bg + yellow-900 text (from Figma badge-warning tokens)
        warning:
          'border-transparent bg-yellow-100 text-yellow-900 shadow-sm focus:ring-yellow-300',
        // Info: blue-200 bg + blue-800 text (from Figma badge-info tokens)
        info: 'border-transparent bg-blue-200 text-blue-800 shadow-sm focus:ring-blue-300',
        // Exceptional: purple-200 bg + purple-800 text (from Figma badge-exceptional tokens)
        exceptional:
          'border-transparent bg-purple-200 text-purple-800 shadow-sm focus:ring-purple-300',
        // Outline variant
        outline: 'border-gray-300 bg-white text-gray-700 focus:ring-gray-400',
      },
      size: {
        default: 'h-6 min-w-6 gap-1 rounded-full px-2 text-xs leading-4',
        compact: 'h-6 min-w-6 gap-1 rounded-full px-2 text-xs leading-4',
        dot: 'h-6 w-6 min-w-6 gap-0 rounded-full p-0 text-[0px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode
}

function Badge({
  className,
  variant,
  size,
  icon,
  children,
  ...props
}: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {icon && (
        <span className="flex h-4 w-4 items-center justify-center">{icon}</span>
      )}
      {children}
    </div>
  )
}

export { Badge, badgeVariants }
